/**
 * Regeln zum Item Shop (Masterprompt Abschnitt 66).
 *
 * Reine Funktionen: Der Client blendet damit Schaltflaechen aus, der Server
 * entscheidet damit wirklich. Geprueft wird immer serverseitig.
 */

import {
  CONSUMABLE_COOLDOWN_SECONDS,
  HERO_ITEM_SLOTS,
  ITEM_SELL_RATIO,
  ITEM_STAGE_COSTS,
  MAX_ITEM_STAGE,
  getConsumable,
  getItem,
  type ItemEffects,
} from '@dueline/game-data';

/** Ein Item im Besitz eines Spielers. */
export interface OwnedItem {
  itemId: string;
  /** 1 bis 3. Besondere Items wie das Totem bleiben auf 1. */
  stage: number;
  /** Gold, das insgesamt hineingeflossen ist - Grundlage fuer den Verkauf. */
  investedGold: number;
  /** Solange groesser 0, ist das Item noch unterwegs und wirkt nicht. */
  deliverySecondsRemaining: number;
}

export type ItemBlock =
  | 'unknown_item'
  | 'not_enough_gold'
  | 'no_free_slot'
  | 'already_owned'
  | 'max_stage'
  | 'not_owned'
  | 'in_delivery'
  | 'no_charges'
  | 'on_cooldown'
  | 'hero_dead'
  | 'max_charges';

export interface ItemCheck {
  allowed: boolean;
  reason: ItemBlock | null;
  /** Was der Vorgang kostet. Beim Verkauf der Erloes. */
  cost: number;
}

function blocked(reason: ItemBlock): ItemCheck {
  return { allowed: false, reason, cost: 0 };
}

/**
 * Kosten der naechsten Stufe. stage 0 heisst: noch nicht gekauft.
 *
 * Ein Item darf teurer sein als der Rest (costFactor) - das Trade Seal ist
 * so eines. Gerundet wird auf ganze Goldstuecke, sonst stuenden im Laden
 * Nachkommastellen.
 */
export function getItemStageCost(itemId: string, stage: number): number {
  const definition = getItem(itemId);
  if (!definition) return 0;
  if (definition.fixedCost !== undefined) return stage === 0 ? definition.fixedCost : 0;

  const grundpreis = ITEM_STAGE_COSTS[stage] ?? 0;
  return Math.round(grundpreis * (definition.costFactor ?? 1));
}

/** Hoechste Stufe dieses Items. Besondere Items haben nur eine. */
export function getMaxStage(itemId: string): number {
  const definition = getItem(itemId);
  if (!definition) return 0;
  return definition.unique ? 1 : MAX_ITEM_STAGE;
}

/** Darf dieser Spieler dieses Item jetzt kaufen? */
export function checkItemPurchase(
  gold: number,
  owned: readonly OwnedItem[],
  itemId: string,
): ItemCheck {
  const definition = getItem(itemId);
  if (!definition) return blocked('unknown_item');
  if (owned.some((item) => item.itemId === itemId)) return blocked('already_owned');
  if (owned.length >= HERO_ITEM_SLOTS) return blocked('no_free_slot');

  const kosten = getItemStageCost(itemId, 0);
  if (gold < kosten) return blocked('not_enough_gold');

  return { allowed: true, reason: null, cost: kosten };
}

/** Darf dieser Spieler dieses Item jetzt ausbauen? */
export function checkItemUpgrade(
  gold: number,
  owned: readonly OwnedItem[],
  itemId: string,
): ItemCheck {
  const vorhanden = owned.find((item) => item.itemId === itemId);
  if (!vorhanden) return blocked('not_owned');
  if (vorhanden.stage >= getMaxStage(itemId)) return blocked('max_stage');

  const kosten = getItemStageCost(itemId, vorhanden.stage);
  if (kosten <= 0) return blocked('max_stage');
  if (gold < kosten) return blocked('not_enough_gold');

  return { allowed: true, reason: null, cost: kosten };
}

/**
 * Verkaufserloes: 65 % des eingesetzten Goldes.
 *
 * Bewusst auf das EINGESETZTE Gold bezogen und nicht auf den Grundpreis -
 * sonst waere ein voll ausgebautes Item beim Verkauf fast wertlos.
 */
export function getSellValue(item: OwnedItem): number {
  return Math.floor(item.investedGold * ITEM_SELL_RATIO);
}

/** Darf dieser Spieler dieses Item jetzt verkaufen? */
export function checkItemSale(owned: readonly OwnedItem[], itemId: string): ItemCheck {
  const vorhanden = owned.find((item) => item.itemId === itemId);
  if (!vorhanden) return blocked('not_owned');
  return { allowed: true, reason: null, cost: getSellValue(vorhanden) };
}

/** Darf dieser Spieler dieses Consumable jetzt kaufen? */
export function checkConsumablePurchase(
  gold: number,
  charges: number,
  consumableId: string,
): ItemCheck {
  const definition = getConsumable(consumableId);
  if (!definition) return blocked('unknown_item');
  if (charges >= definition.maxCharges) return blocked('max_charges');
  if (gold < definition.cost) return blocked('not_enough_gold');
  return { allowed: true, reason: null, cost: definition.cost };
}

/** Darf dieser Spieler jetzt trinken? */
export function checkConsumableUse(
  charges: number,
  cooldownRemaining: number,
  alive: boolean,
  consumableId: string,
): ItemCheck {
  if (!getConsumable(consumableId)) return blocked('unknown_item');
  if (!alive) return blocked('hero_dead');
  if (charges <= 0) return blocked('no_charges');
  if (cooldownRemaining > 0) return blocked('on_cooldown');
  return { allowed: true, reason: null, cost: 0 };
}

/** Gemeinsame Abklingzeit nach dem Trinken. */
export function getConsumableCooldown(): number {
  return CONSUMABLE_COOLDOWN_SECONDS;
}

/**
 * Zaehlt die Wirkung aller FERTIG gelieferten Items zusammen.
 *
 * Was noch unterwegs ist, zaehlt nicht - sonst waere die Lieferzeit wirkungslos.
 */
export function sumItemEffects(owned: readonly OwnedItem[]): Required<ItemEffects> {
  const summe: Required<ItemEffects> = {
    maxHpRatio: 0,
    attackDamageRatio: 0,
    maxManaRatio: 0,
    moveSpeedRatio: 0,
    armor: 0,
    magicResistance: 0,
    manaRegen: 0,
    hpRegenRatio: 0,
    cooldownReduction: 0,
    healOnKillRatio: 0,
    sentCreepHpRatio: 0,
    incomeRatio: 0,
    sentCreepDamageRatio: 0,
  };

  for (const item of owned) {
    if (item.deliverySecondsRemaining > 0) continue;

    const definition = getItem(item.itemId);
    const stufe = definition?.stages[Math.max(0, item.stage - 1)];
    if (!stufe) continue;

    summe.maxHpRatio += stufe.maxHpRatio ?? 0;
    summe.attackDamageRatio += stufe.attackDamageRatio ?? 0;
    summe.maxManaRatio += stufe.maxManaRatio ?? 0;
    summe.moveSpeedRatio += stufe.moveSpeedRatio ?? 0;
    summe.armor += stufe.armor ?? 0;
    summe.magicResistance += stufe.magicResistance ?? 0;
    summe.manaRegen += stufe.manaRegen ?? 0;
    summe.hpRegenRatio += stufe.hpRegenRatio ?? 0;
    summe.cooldownReduction += stufe.cooldownReduction ?? 0;
    summe.healOnKillRatio += stufe.healOnKillRatio ?? 0;
    summe.sentCreepHpRatio += stufe.sentCreepHpRatio ?? 0;
    summe.incomeRatio += stufe.incomeRatio ?? 0;
    summe.sentCreepDamageRatio += stufe.sentCreepDamageRatio ?? 0;
  }

  // Abklingzeiten lassen sich nicht beliebig kuerzen - sonst waeren Skills
  // irgendwann dauerhaft verfuegbar.
  summe.cooldownReduction = Math.min(0.4, summe.cooldownReduction);
  return summe;
}

/** Hat der Spieler ein einsatzbereites Revive Totem? */
export function hasReviveTotem(owned: readonly OwnedItem[]): boolean {
  return owned.some((item) => item.itemId === 'revive_totem');
}
